import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

import { useQueryClient } from '@tanstack/react-query';
import {
  ZfBox,
  ZfDialog,
  ZfDialogContent,
  ZfDialogFooter,
  ZfDialogHeader,
  ZfDivider,
  ZfFormField,
  ZfIcon,
  ZfInput,
  ZfLink,
  ZfLoadingSpinner,
  ZfPopover,
  ZfSelectOption,
  ZfStack,
  ZfTextButton,
  // ZfToggle,
  ZfTypography,
  ZfBanner,
} from '@zafire/react';
import { Formik, setNestedObjectValues } from 'formik';
import {
  setCnError,
  setFilterSearch,
  setLoadingStatus,
  setNoteDisplayDialog,
  setShownNote,
  setToastMessage,
  toggleContactNoteDrawer,
} from 'reducers/contactNoteListReducer';
import { initContactNotes, updateContactNote } from 'reducers/contactNoteReducer';
import { GlobalState } from 'reducers/stateTypes';
import { selectIsUsedInBr } from 'selectors/contactNoteListSelectors';
import ContactNote, { ContactNoteDialog } from 'types/ContactNote';
import { User } from 'types/User';
import { getRmFullName } from 'utils/contactNoteUtil';
import { ContactNoteStatus } from 'views/ContactNotes/components/ContactNoteRows';
import { DisplayDialogs } from 'views/ContactNotes/ContactNotesContainer';
import contactNoteDialogService from 'services/contactNoteDialogService';
import { EnvConfig } from 'config/envConfig';
import { ObjectSchema } from 'yup';
import { useGetMultiplePartnerDetails } from 'hooks/useGetMultiplePartnerDetails';
import { useOpenContactNote } from 'hooks/useOpenContactNote';
import { DOCUMENT_COUNTRY_GUIDELINES, DOCUMENT_JBG_2048_00, DOCUMENT_JBG_2038_00 } from 'services/constants';
import { isBRFieldMandatoryCheck } from 'utils/noteDialogUtil';

import AttendeesBr from './components/AttendeesBr';
import ContactDetails from './components/ContactDetails';
import SubmissionDialog from './components/SubmissionDialog';
import { ContactNoteDialogStatus } from './components/data/ContactNoteDialogStatus';
import ErrorDialog from './components/ErrorDialog';
import DropZone from './components/DropZone';
import { ContactType } from './components/data/ContactNoteDialogOptions';

export type DialogControl = { showDialog: () => void };

interface ContactNotesFormDialogProps {
  edit?: boolean;
  show: boolean;
  onClose: () => void;
  initialValues: ContactNoteDialog;
  validationSchema: ObjectSchema<any>;
  isPartnerBR?: boolean;
  isDragAndDrop?: boolean;
  isFederated?: boolean;
  brUId?: string | undefined;
  partnerUId?: string | undefined;
}

const DROPDOWN_HEIGHT = 32;
const ContactNotesFormDialog = ({
  edit = false,
  show,
  onClose,
  initialValues,
  validationSchema,
  isPartnerBR = true, // true means user is in partner/br screen
  isDragAndDrop = false,
  isFederated = false,
  brUId = undefined,
  partnerUId = undefined,
}: ContactNotesFormDialogProps) => {
  const dispatch = useDispatch();
  const [isConfirmationPassed, setConfirmationPassed] = useState(false);
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [notifySuggestion, setNotifySuggestion] = useState('');
  const [shouldNoteSubmit, setNoteSubmit] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [errorObject, setErrorObject] = useState<any>(undefined);
  const queryClient = useQueryClient();
  const bannerRef = useRef<any>(null);

  const { t } = useTranslation();
  const openContactNote = useOpenContactNote();

  const usedInBrFromStore = useSelector(selectIsUsedInBr);
  const usedInBr = isFederated ? brUId !== null : usedInBrFromStore;

  const user: User = useSelector((state: GlobalState) => state.user.userDetails);
  const dateRange: any[] = useSelector((state: GlobalState) => state.contactNoteList.dateRange);
  const subjectSearch: string = useSelector((state: GlobalState) => state.contactNoteList.subjectSearch);
  const employeeOptions = useSelector((state: GlobalState) => state.resources.employeeOptions);

  const params = useParams<{ techId: string }>();
  const techId = isFederated ? brUId || partnerUId : params.techId;

  //This is for compliance user
  const nonEditable = useMemo(() => {
    return !user.hasNoteEditAccess && edit;
  }, [user.hasNoteEditAccess, edit]);

  const [showDialog, setShowDialog] = useState(show);

  useEffect(() => {
    if (show && !showDialog) {
      setShowDialog(true);
    }
  }, [show]);

  useEffect(() => {
    if (showErrorBanner && bannerRef.current) {
      bannerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showErrorBanner]);

  const notifyFilteredOptions = useMemo(() => {
    const suggestion = notifySuggestion.toLowerCase();
    const data = employeeOptions.filter((option) => option.label.toLowerCase().includes(suggestion));
    return data.length > 50 ? data.slice(0, 50) : data;
  }, [employeeOptions, notifySuggestion]);

  //The api required parameters are not confirmed yet, replicated from ContactNoteFormContainer
  return (
    <Formik
      validateOnBlur={false}
      validateOnChange
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (data, { setSubmitting, resetForm }) => {
        setSubmitting(true);
        setShowErrorBanner(false);
        if (edit && isConfirmationPassed) {
          try {
            const updatedData: ContactNote = await contactNoteDialogService.updateContactNote({
              ...data,
              ...(EnvConfig.config.newSubmissionStatus && {
                status: user.hasUpdateQualifiedContact
                  ? data.status
                  : shouldNoteSubmit
                    ? ContactNoteDialogStatus.INSUBMISSION
                    : ContactNoteDialogStatus.DRAFT,
              }),
              ...(!EnvConfig.config.newSubmissionStatus && {
                status: shouldNoteSubmit ? ContactNoteStatus.INSUBMISSION : ContactNoteStatus.EDITABLE,
              }),
            });

            if (
              (data.notifyOtherUser?.length ?? 0) > 0 &&
              !user.hasUpdateQualifiedContact &&
              shouldNoteSubmit &&
              data.status === ContactNoteDialogStatus.DRAFT
            ) {
              await contactNoteDialogService.sendNotification({
                userId: data.notifyOtherUser as string[],
                brUID: usedInBr ? techId || '' : '',
                partnerUID: usedInBr ? '' : techId,
                contactNoteId: updatedData.contactNoteId,
              });
            }
            dispatch(
              updateContactNote({
                ...updatedData,
                partnerFullName: usedInBr ? '' : data.partner.fullName,
                responsibleRmFullName: usedInBr ? '' : getRmFullName(data),
              }),
            );

            dispatch(
              setShownNote({
                ...updatedData,
                partnerFullName: usedInBr ? '' : data.partner.fullName,
                responsibleRmFullName: usedInBr ? '' : getRmFullName(data),
              }),
            );
            setNoteSubmit(false);
            setConfirmationPassed(false);
            resetForm();
            dispatch(setNoteDisplayDialog(DisplayDialogs.DEFAULT_NOTE_VIEW));
            if (!edit) {
              dispatch(toggleContactNoteDrawer(false));
            }
          } catch (e) {
            setErrorObject(e);
            // setShowErrorDialog(true)
            setShowErrorBanner(true);
          } finally {
            if (EnvConfig.config.useAgGridV2HomeSearch) {
              queryClient.invalidateQueries(['audit-history']);
              queryClient.invalidateQueries(['searchNotesAgGridV2']);
            }

            setSubmitting(false);
            setIsSaving(false);
            setIsSubmittingForm(false);
            dispatch(setLoadingStatus(false));
          }
        } else {
          try {
            if (isConfirmationPassed || !shouldNoteSubmit) {
              const response: ContactNote = await contactNoteDialogService.create({
                ...data,
                ...(EnvConfig.config.newSubmissionStatus && {
                  status: shouldNoteSubmit ? ContactNoteDialogStatus.INSUBMISSION : ContactNoteDialogStatus.DRAFT,
                }),
                ...(!EnvConfig.config.newSubmissionStatus && {
                  status: shouldNoteSubmit
                    ? ContactNoteStatus.INSUBMISSION
                    : edit
                      ? ContactNoteStatus.EDITABLE
                      : ContactNoteStatus.CREATED,
                }),
              });
              if ((data.notifyOtherUser?.length ?? 0) > 0 && shouldNoteSubmit) {
                await contactNoteDialogService.sendNotification({
                  userId: data.notifyOtherUser as string[],
                  brUID: usedInBr ? techId || '' : '',
                  partnerUID: usedInBr ? '' : techId,
                  contactNoteId: response.contactNoteId,
                });
              }

              dispatch(setCnError(''));

              setNoteSubmit(false);
              setConfirmationPassed(false);
              resetForm();
              dispatch(setNoteDisplayDialog(DisplayDialogs.DEFAULT_NOTE_VIEW));
              onClose();
              dispatch(setLoadingStatus(true));
              if (!edit) {
                dispatch(toggleContactNoteDrawer(false));
              }

              const openCreatedNoteFromToast = () => {
                dispatch(
                  setFilterSearch({
                    id: response?.contactNoteId || '',
                    tripId: '',
                    additionalAttendees: '',
                    relatedPartners: '',
                    source: '',
                    relatedBRs: '',
                    startDate: '',
                    endDate: '',
                    status: '',
                  }),
                );
                openContactNote({
                  ...response,
                  partnerFullName: usedInBr ? '' : data.partner.fullName,
                  responsibleRmFullName: usedInBr ? '' : getRmFullName(data),
                });
              };

              dispatch(
                setToastMessage({
                  type: 'success',
                  show: true,
                  titleText: shouldNoteSubmit
                    ? t('contactNote.toastMessage.submitted', { name: response?.contactNoteId || '' })
                    : t('contactNote.toastMessage.created', { name: response?.contactNoteId || '' }),
                  ...(!isFederated && {
                    actionButtonText: t('contactNote.toastMessage.openContactNoteButton'),
                    onActionClick: openCreatedNoteFromToast,
                  }),
                }),
              );
            }
          } catch (e) {
            setErrorObject(e);
            setShowErrorBanner(true);
          } finally {
            if (EnvConfig.config.useAgGridV2HomeSearch) {
              queryClient.invalidateQueries(['audit-history']);
              queryClient.invalidateQueries(['searchNotesAgGridV2']);
            } else {
              try {
                const notes = await contactNoteDialogService.getMyNotes(
                  '',
                  false,
                  subjectSearch,
                  dateRange,
                  '',
                  techId,
                  usedInBr,
                );

                dispatch(initContactNotes(notes));
              } catch (e) {
                setErrorObject(e);
                setShowErrorBanner(true);
              }
            }
            setSubmitting(false);
            setIsSaving(false);
            setIsSubmittingForm(false);
            dispatch(setLoadingStatus(false));
          }
        }
      }}
    >
      {({
        setFieldValue,
        values,
        getFieldProps,
        errors,
        setErrors,
        submitForm,
        resetForm,
        touched,
        isSubmitting,
        validateForm,
        setTouched,
      }) => {
        const hideSaveDraft = useMemo(() => {
          return [ContactNoteDialogStatus.INSUBMISSION, ContactNoteDialogStatus.SUBMITTED].includes(
            values.status as ContactNoteDialogStatus,
          );
        }, [values.status]);

        const relatedPartnersValue = useMemo(() => {
          if (isPartnerBR) {
            return values.relatedPartners.map((item) => item?.value).filter((item) => item !== techId);
          }
          return values.relatedPartners.map((item) => item.value);
        }, [values.relatedPartners, values?.partner?.partnerUId, isPartnerBR]);

        const { data: multiplePartnerDetails, isFetching: isMultiplePartnerDetailsLoading } =
          useGetMultiplePartnerDetails(relatedPartnersValue, !isPartnerBR);

        const isActivePartner = useMemo(() => {
          if (multiplePartnerDetails) {
            return isBRFieldMandatoryCheck(multiplePartnerDetails);
          }

          return false;
        }, [multiplePartnerDetails]);

        //For the new requirement the validation is bypass only if the user is compliance
        const handleSaveDraft = async () => {
          setIsSaving(true);
          setTouched({});
          try {
            const updatedData: ContactNote = await contactNoteDialogService.updateContactNote({
              ...values,
              status: values.status,
            });
            dispatch(
              updateContactNote({
                ...updatedData,
                partnerFullName: usedInBr ? '' : values.partner.fullName,
                responsibleRmFullName: usedInBr ? '' : getRmFullName(values),
              }),
            );
            dispatch(
              setShownNote({
                ...updatedData,
                partnerFullName: usedInBr ? '' : values.partner.fullName,
                responsibleRmFullName: usedInBr ? '' : getRmFullName(values),
              }),
            );
            setNoteSubmit(false);
            setConfirmationPassed(false);
            resetForm();
            dispatch(setNoteDisplayDialog(DisplayDialogs.DEFAULT_NOTE_VIEW));
            if (!edit) {
              dispatch(toggleContactNoteDrawer(false));
            }
          } catch (e) {
            if (e instanceof Error) {
              setErrorObject(e);
            } else {
              setErrorObject(new Error(String(e))); // fallback for non-Error types
            }
            setShowErrorBanner(true);
          } finally {
            if (EnvConfig.config.useAgGridV2HomeSearch) {
              queryClient.invalidateQueries(['audit-history']);
              queryClient.invalidateQueries(['searchNotesAgGridV2']);
            }
            setIsSaving(false);
            setIsSubmittingForm(false);
          }
        };

        const validateAndTouched = async () => {
          const errors = await validateForm();
          if (!isPartnerBR) {
            const relatedPartnersList = values.relatedPartners.filter((item) => item.value !== 'other');
            if (relatedPartnersList.length === 0) {
              errors['relatedPartners'] = t('contactNote.validation.relatedPartners');
            }

            if (isActivePartner && values.relatedBRs.length === 0) {
              errors['relatedBRs'] = t('contactNote.validation.relatedBrs');
            }
            if (!isActivePartner) {
              const hasOther = relatedPartnersList.find((item) => item.value === 'other');
              const hasPI = relatedPartnersList.find((item) => item.type === 'PI');
              if (
                !hasPI &&
                !hasOther &&
                ![ContactType.EMAIL, ContactType.LETTER, ContactType.FAX, ContactType.OTHER].includes(
                  values.contactType as ContactType,
                )
              ) {
                errors['relatedPartners'] = t('contactNote.validation.privateIndividualPartner');
              }
            }

            if (Object.keys(errors)?.length > 0) {
              setErrors(errors);
            }
          }

          if (Object.keys(errors)?.length === 0) {
            setConfirmationPassed(true);
            setIsSaving(true);
            submitForm();
            return;
          }

          return setTouched(setNestedObjectValues(errors, true));
        };

        const validateAndShowSubmitDialog = async () => {
          const errors = await validateForm();
          if (!isPartnerBR) {
            const relatedPartnersList = values.relatedPartners.filter((item) => item.value !== 'other');
            if (relatedPartnersList.length === 0) {
              errors['relatedPartners'] = t('contactNote.validation.relatedPartners');
            }

            if (isActivePartner && values.relatedBRs.length === 0) {
              errors['relatedBRs'] = t('contactNote.validation.relatedBrs');
            }
            if (!isActivePartner) {
              const hasOther = relatedPartnersList.find((item) => item.value === 'other');
              const hasPI = relatedPartnersList.find((item) => item.type === 'PI');
              if (
                !hasPI &&
                !hasOther &&
                ![ContactType.EMAIL, ContactType.LETTER, ContactType.FAX, ContactType.OTHER].includes(
                  values.contactType as ContactType,
                )
              ) {
                errors['relatedPartners'] = t('contactNote.validation.privateIndividualPartner');
              }
            }
            if (Object.keys(errors)?.length > 0) {
              setErrors(errors);
            }
          }
          if (Object.keys(errors)?.length === 0) {
            setNoteSubmit(true);
            return;
          }
          setTouched(setNestedObjectValues(errors, true));
        };

        const showNotifyField = useMemo(() => {
          return ![ContactNoteDialogStatus.INSUBMISSION, ContactNoteDialogStatus.SUBMITTED].includes(
            values.status as ContactNoteDialogStatus,
          );
        }, [values.notifyOtherUser]);

        return (
          <>
            {showDialog && (
              <ZfDialog
                show={show}
                size="large"
                onDialogClose={() => {
                  onClose();
                  resetForm();
                  setShowErrorBanner(false);
                }}
                disableLightDismiss
              >
                <ZfDialogHeader>
                  {edit ? t('contactNote.editTitle') : t('contactNote.createContactNote')}
                </ZfDialogHeader>
                <ZfDialogContent>
                  <ZfStack spacing="extra-extra-small">
                    {/* Not included in the current phase */}
                    {showErrorBanner && (
                      <ZfBanner
                        ref={bannerRef}
                        style={{ textWrap: 'wrap', whiteSpace: 'pre-line', paddingBottom: 'var(--gap-vertical-xxl)' }}
                        type="error"
                        titleText={t('contactNote.form.banner.title')}
                        description={t('contactNote.form.banner.description')}
                        dismissible={false}
                        actionButtonText={t('contactNote.form.banner.viewDetails')}
                        onBannerActionButtonClick={() => setShowErrorDialog(true)}
                      />
                    )}
                    <ZfFormField
                      label={t('contactNote.form.subject')}
                      style={{ minHeight: 78 }}
                      mandatory
                      alerts={[errors.subject ?? '']}
                    >
                      <ZfInput
                        {...getFieldProps('subject')}
                        placeholder={t('contactNote.form.placeholder.label.subject')}
                        onInputChange={(e) => setFieldValue('subject', e.detail)}
                        name="subject"
                        data-testid="subject-text-input"
                        id="subject-text-input"
                        value={values.subject}
                        counter
                        error={!!errors.subject && touched.subject}
                        maxLength={350}
                        disabled={nonEditable}
                      />
                    </ZfFormField>

                    <ZfFormField
                      label={t('contactNote.form.notes')}
                      style={{ minHeight: 178 }}
                      mandatory
                      alerts={[errors.notes ?? '']}
                    >
                      <ZfInput
                        {...getFieldProps('notes')}
                        multiline
                        rows={6}
                        type="text"
                        placeholder={t('contactNote.form.placeholder.label.notes')}
                        onInputChange={getFieldProps('notes').onChange}
                        name="notes"
                        data-testid="notes-text-input"
                        id="notes-text-input"
                        value={values.notes}
                        error={!!errors.notes && touched.notes}
                        disabled={nonEditable}
                      />
                      <ZfPopover slot="label-trailing" alignment="start" data-testid="notes-popover">
                        <ZfIcon slot="anchor" size="medium" name="info" style={{ cursor: 'pointer' }}></ZfIcon>
                        <ZfBox styles={{ textWrap: 'wrap' }}>
                          <ZfTypography style={{ whiteSpace: 'pre-line' }} variant="label-tiny" color="normal">
                            <Trans
                              i18nKey="contactNote.form.popover.notes.label"
                              components={{
                                u: <u />,
                                strong: <ZfTypography variant="label-small" weight="bold" />,
                                1: (
                                  <ZfLink
                                    size="extra-small"
                                    type="inline"
                                    target="_blank"
                                    rel="noreferrer"
                                    href={DOCUMENT_JBG_2038_00}
                                  />
                                ),
                                2: (
                                  <ZfLink
                                    size="extra-small"
                                    type="inline"
                                    target="_blank"
                                    rel="noreferrer"
                                    href={DOCUMENT_COUNTRY_GUIDELINES}
                                  />
                                ),
                                3: (
                                  <ZfLink
                                    size="extra-small"
                                    type="inline"
                                    target="_blank"
                                    rel="noreferrer"
                                    href={DOCUMENT_JBG_2048_00}
                                  />
                                ),
                              }}
                            ></Trans>
                          </ZfTypography>
                        </ZfBox>
                      </ZfPopover>
                    </ZfFormField>

                    <AttendeesBr
                      edit={edit}
                      nonEditable={nonEditable}
                      isPartnerBR={isPartnerBR}
                      isFederated={isFederated}
                      brUId={brUId}
                      partnerUId={partnerUId}
                    />
                    <ContactDetails nonEditable={nonEditable} edit={edit} isDragAndDrop={isDragAndDrop} />
                    <ZfStack direction="row" spacing="small" style={{ paddingBottom: 'var(--gap-vertical-md)' }}>
                      <ZfTypography variant="label-medium" color="emphasis" weight="bold">
                        {t('contactNote.form.documentUpload')}
                      </ZfTypography>
                      <ZfDivider style={{ flex: 1, paddingTop: 'var(--padding-top-sm)' }} />
                    </ZfStack>
                    <DropZone nonEditable={nonEditable} />
                    {showNotifyField && (
                      <>
                        {' '}
                        <ZfStack
                          direction="row"
                          spacing="small"
                          style={{ paddingTop: 'var(--gap-vertical-xs)', paddingBottom: 'var(--gap-vertical-md)' }}
                        >
                          <ZfTypography variant="label-medium" color="emphasis" weight="bold">
                            {t('contactNote.form.notifyUser')}
                          </ZfTypography>
                          <ZfDivider style={{ flex: 1, paddingTop: 'var(--padding-top-sm)' }} />
                        </ZfStack>
                        <ZfFormField style={{ flex: 1, minHeight: 78 }} label={t('contactNote.form.notifyOtherUser')}>
                          <ZfInput
                            type="combo-box"
                            placeholder={t('contactNote.form.placeholder.selectOrEnter', {
                              name: t('contactNote.form.placeholder.label.name'),
                            })}
                            allowSearch
                            onInputDropdownOpen={() => setNotifySuggestion('')}
                            onInputChange={(e) => setNotifySuggestion(e.detail)}
                            name="notifyOtherUser"
                            onSelectChange={getFieldProps('notifyOtherUser').onChange}
                            disabled={nonEditable}
                            value={values.notifyOtherUser}
                            getOptionLabel={(option) =>
                              employeeOptions.find((co) => co.value === option)?.label || `N/A (${option})`
                            }
                            data-testid="notify-other"
                          >
                            <FixedSizeList
                              itemSize={DROPDOWN_HEIGHT}
                              itemCount={notifyFilteredOptions.length}
                              overscanCount={5}
                              height={Math.min(notifyFilteredOptions.length, 10) * DROPDOWN_HEIGHT}
                              width="100%"
                            >
                              {(props: ListChildComponentProps) => {
                                const { index, style } = props;
                                const co = notifyFilteredOptions[index];

                                return (
                                  <ZfSelectOption
                                    style={style}
                                    key={co.value}
                                    value={co.value}
                                    disabled={edit && co.value === techId}
                                    isSelected={values?.notifyOtherUser?.includes(co.value)}
                                  >
                                    {co.label}
                                  </ZfSelectOption>
                                );
                              }}
                            </FixedSizeList>
                          </ZfInput>
                          <ZfPopover slot="label-trailing" alignment="start">
                            <ZfIcon slot="anchor" size="medium" name="info" style={{ cursor: 'pointer' }}></ZfIcon>
                            <ZfBox styles={{ textWrap: 'wrap' }}>
                              <ZfTypography variant="label-tiny" color="normal">
                                {t('contactNote.form.notifyOtherUserPopover')}
                              </ZfTypography>
                            </ZfBox>
                          </ZfPopover>
                        </ZfFormField>
                      </>
                    )}
                  </ZfStack>
                </ZfDialogContent>
                <ZfDialogFooter>
                  <ZfStack slot="primary" direction="row" spacing="small">
                    <ZfTextButton
                      size="medium"
                      hierarchy="tertiary"
                      onClick={() => {
                        if (!isSubmitting && !isSaving && !isSubmittingForm) {
                          resetForm();
                          setShowErrorBanner(false);
                          onClose();
                        }
                      }}
                      disabled={isSubmitting || isSaving || isSubmittingForm}
                      data-testid="cancel-button"
                    >
                      {t('contactNote.form.cancel')}
                    </ZfTextButton>
                    {!hideSaveDraft && (
                      <ZfTextButton
                        size="medium"
                        hierarchy="secondary"
                        data-testid="save-draft-button"
                        onClick={async () => {
                          if (!isSubmitting && !isSaving && !isSubmittingForm) {
                            setShowErrorBanner(false);
                            if (user.hasUpdateQualifiedContact) {
                              await handleSaveDraft();
                            } else {
                              await validateAndTouched();
                            }
                          }
                        }}
                        disabled={isSubmitting || isMultiplePartnerDetailsLoading}
                      >
                        {isSaving && <ZfLoadingSpinner size="small"></ZfLoadingSpinner>}
                        {t('contactNote.form.saveDraft')}
                      </ZfTextButton>
                    )}
                    <ZfTextButton
                      size="medium"
                      onClick={async () => {
                        if (!isSubmitting && !isSaving && !isSubmittingForm) {
                          setShowErrorBanner(false);
                          if (user.hasUpdateQualifiedContact) {
                            await handleSaveDraft();
                          } else {
                            await validateAndShowSubmitDialog();
                          }
                        }
                      }}
                      data-testid="submit-button"
                      disabled={
                        isSubmitting ||
                        (user.hasUpdateQualifiedContact && values.status === ContactNoteDialogStatus.DRAFT) ||
                        isMultiplePartnerDetailsLoading
                      }
                    >
                      {isSubmittingForm && <ZfLoadingSpinner size="small" />}
                      {t('contactNote.form.submit')}
                    </ZfTextButton>
                  </ZfStack>
                </ZfDialogFooter>
              </ZfDialog>
            )}
            <SubmissionDialog
              loading={isSubmitting}
              showDialog={shouldNoteSubmit}
              onDialogClose={() => setNoteSubmit(false)}
              onProceed={() => {
                if (!isSubmitting) {
                  setIsSubmittingForm(true);
                  setConfirmationPassed(true);
                  submitForm();
                }
              }}
            />
            <ErrorDialog
              error={errorObject}
              showDialog={showErrorDialog}
              onDialogClose={() => setShowErrorDialog(false)}
            />
          </>
        );
      }}
    </Formik>
  );
};

export default ContactNotesFormDialog;
